import React, { useState } from 'react';
import { Goal } from '../types';
import {
  REFLECTION_BLOCKS,
  selectVariant,
  ReflectionVariant
} from './ai/reflectionPrompts';

interface ReflectionQuestionsProps {
  goal: Goal;
  onClose?: () => void;
}

/**
 * ReflectionQuestions - Standalone component voor reflectievragen
 * 
 * Neutrale vragen om externe feedback en markttoetsing te verkennen.
 * GEEN diagnose, GEEN labels, GEEN conclusies.
 * Alleen reflectie faciliteren.
 */
const ReflectionQuestions: React.FC<ReflectionQuestionsProps> = ({
  goal,
  onClose
}) => {
  const [selectedVariant, setSelectedVariant] = useState<ReflectionVariant>(
    selectVariant(goal.description)
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            Reflectie: Externe input & spiegeling
          </h3>
          <p className="text-sm text-gray-600">
            Voor doel: {goal.title}
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Sluiten"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Variant selector (subtle) */}
      <div className="flex items-center space-x-2 text-xs">
        <span className="text-gray-500">Taalstijl:</span>
        <button
          onClick={() => setSelectedVariant('practical')}
          className={`px-2 py-1 rounded transition-colors ${
            selectedVariant === 'practical'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Praktisch
        </button>
        <button
          onClick={() => setSelectedVariant('neutral')}
          className={`px-2 py-1 rounded transition-colors ${
            selectedVariant === 'neutral'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Neutraal
        </button>
        <button
          onClick={() => setSelectedVariant('reflective')}
          className={`px-2 py-1 rounded transition-colors ${
            selectedVariant === 'reflective'
              ? 'bg-blue-100 text-blue-700'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Reflectief
        </button>
      </div>

      {/* Intro */}
      <div className="text-sm text-gray-700 leading-relaxed">
        De volgende vragen helpen je om te kijken hoeveel externe input en spiegeling er nu al is rondom dit doel.
      </div>

      {/* Question blocks */}
      <div className="space-y-6">
        {REFLECTION_BLOCKS.map((block, blockIndex) => (
          <div key={blockIndex} className="space-y-3">
            <h4 className="text-sm font-medium text-gray-800">
              {block.title}
            </h4>
            <div className="space-y-3 pl-4 border-l-2 border-gray-200">
              {block.questions.map((question, qIndex) => (
                <div key={question.id} className="space-y-2">
                  <p className="text-sm text-gray-700">
                    {blockIndex + 1}.{qIndex + 1} {question.variants[selectedVariant]}
                  </p>
                  {/* Optional: Add textarea for user to type answers */}
                  <textarea
                    placeholder="Jouw reflectie... (optioneel)"
                    className="
                      w-full px-3 py-2 text-sm
                      border border-gray-200 rounded-md
                      focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
                      resize-none
                    "
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Outro */}
      <div className="text-sm text-gray-600 italic pt-4 border-t border-gray-100">
        Neem even de tijd om te voelen of dit genoeg externe spiegeling geeft voor waar je nu staat.
      </div>

      {/* Footer note */}
      <div className="text-xs text-gray-400 pt-2">
        💡 Deze vragen zijn alleen voor reflectie. Je antwoorden worden niet opgeslagen en beïnvloeden geen automatische processen.
      </div>
    </div>
  );
};

export default ReflectionQuestions;
